import { useEffect, useState } from 'react';
import { NavLink, useLocation, useResolvedPath } from 'react-router-dom';
import { FiLogIn, FiMenu, FiX } from 'react-icons/fi';
import CoatOfArms from '../assets/img/LOGO.png';
import { NAV_LINKS, SITE_NAME } from '../config/siteConfig';

// Scroll distance after which the Overview header leaves the hero and turns solid
const SOLID_AFTER_PX = 24;

const trimSlash = (path) => path.replace(/\/+$/, '');

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);

  // The public app is mounted under a base path (e.g. /public), so compare
  // against this layout route's resolved path rather than a hard-coded '/'.
  const { pathname } = useLocation();
  const basePath     = useResolvedPath('').pathname;
  const isOverview   = trimSlash(pathname) === trimSlash(basePath);

  useEffect(() => {
    if (!isOverview) return undefined;
    const onScroll = () => setScrolled(window.scrollY > SOLID_AFTER_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isOverview]);

  // Transparent over the Overview hero photo. Inner pages (Figma 1140:28372)
  // sit on the cream page background with no divider; the Overview header
  // turns white once scrolled or while the mobile menu is open.
  const overHero = isOverview && !scrolled && !mobileOpen;

  const position = isOverview ? 'fixed inset-x-0 top-0' : 'sticky top-0';
  const surface  = overHero
    ? 'border-transparent bg-transparent'
    : isOverview
      ? 'border-gray-100 bg-white'
      : 'border-transparent bg-[#f8f7f2]';
  // Figma "Head Bar - Nav": 90px over the Overview hero (1140:28342),
  // 120px on the inner pages (1140:28372)
  const height   = isOverview ? 'lg:h-[90px]' : 'lg:h-[120px]';

  return (
    <header className={`${position} z-20 border-b transition-colors duration-300 ${surface}`}>
      <div className={`mx-auto flex h-14 max-w-[1440px] items-center gap-4 px-4 sm:px-8 lg:px-10 pub-xl:px-[100px] ${height}`}>
        {/* Logo */}
        <NavLink to="" end className="flex min-w-0 shrink-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md shadow-[0_4px_4px_rgba(26,121,12,0.10)]">
            <img src={CoatOfArms} alt="" className="h-full w-full object-contain" />
          </div>
          <span
            className={`max-w-[120px] text-xs font-normal leading-tight sm:max-w-[180px] sm:text-sm pub-xl:max-w-[191px] pub-xl:text-[14px] ${
              overHero ? 'text-white' : 'text-[#0f3a26]'
            }`}
          >
            {SITE_NAME}
          </span>
        </NavLink>

        {/* Desktop nav */}
        {/* Figma: 13px links, 10px/5px padding, 12px apart, centred between
            logo and Login — sized by its links, so all six fit on one row */}
        <nav className="mx-auto hidden items-center gap-1 lg:flex pub-xl:gap-3">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to || 'overview'}
              to={link.to}
              end={link.to === ''}
              className={({ isActive }) => {
                const base = 'whitespace-nowrap rounded-full px-2 py-[5px] text-[13px] transition-colors pub-xl:px-[10px]';
                if (overHero) {
                  return `${base} text-white ${
                    isActive ? 'bg-[rgba(66,234,41,0.1)] font-medium' : 'font-normal hover:bg-white/10'
                  }`;
                }
                return `${base} ${
                  isActive
                    ? 'bg-[rgba(66,234,41,0.1)] font-medium text-black'
                    : 'font-normal text-black hover:bg-black/5'
                }`;
              }}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Login + mobile toggle */}
        <div className="ml-auto flex shrink-0 items-center gap-2 lg:ml-0">
          <NavLink
            to="/login"
            className="hidden items-center gap-[10px] rounded-full bg-[rgba(66,234,41,0.1)] px-[15px] py-[5px] text-[13px] font-normal text-[#2fda15] transition-colors hover:bg-[rgba(66,234,41,0.2)] lg:flex"
          >
            Login
            <FiLogIn className="h-[15px] w-[15px]" />
          </NavLink>

          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            className={`flex h-9 w-9 items-center justify-center rounded-full lg:hidden ${
              overHero
                ? 'text-white hover:bg-white/10'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
            }`}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <FiX className="h-5 w-5" /> : <FiMenu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="flex flex-col gap-1 border-t border-gray-100 px-4 py-3 lg:hidden">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to || 'overview'}
              to={link.to}
              end={link.to === ''}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[rgba(26,121,12,0.1)] text-[#135809]'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <NavLink
            to="/login"
            onClick={() => setMobileOpen(false)}
            className="mt-1 rounded-lg px-3 py-2 text-sm font-medium text-[#135809] hover:bg-[rgba(26,121,12,0.08)]"
          >
            Login
          </NavLink>
        </nav>
      )}
    </header>
  );
}
